import React, { useState, useEffect, createContext } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebaseClient';



// Components
import Navbar from './components/Navbar';
import Dashboard from './components/Dashboard';
import MobileDashboard from './components/MobileDashboard'; // <-- NEU importieren!
import Properties from './components/Properties';
import Contact from './components/Contact';
import Login from './components/Login';
import Signup from './components/Signup';
import Settings from './components/Settings';
import Documents from './components/Documents';

// Footer + Fuß-Bereich
import Footer from './components/Fuß/Footer';
import AGB from './components/Fuß/AGB';
import Datenschutz from './components/Fuß/Datenschutz';
import Impressum from './components/Fuß/Impressum';

export const CustomerContext = createContext();

/* 
  Geschützte Route (wie gehabt):
  - erfordert Login
  - setzt user/isAdmin 
*/
function ProtectedRoute({ children }) {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);


  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (usr) => {
      if (usr) {
        setUser(usr);
        // ggf. Admin-Flag lesen
        const tokenResult = await usr.getIdTokenResult();
        setIsAdmin(!!tokenResult.claims.admin);
      } else {
        setUser(null);
        setIsAdmin(false);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading) {
    return <div>Loading...</div>;
  }
  if (!user) {
    return <Navigate to="/login" />;
  }

  return children;
}

function ExternalRedirect({ url }) {
  useEffect(() => {
    window.location.replace(url);
  }, [url]);
  return null;
}

/* 
  DemoRoute:
  - Kein Login nötig
  - setzt selectedCustomerId = "10001"
  - Rendert Kinder (Navbar + Page + Footer) 
*/
function DemoRoute({ children }) {
  const { setSelectedCustomerId } = React.useContext(CustomerContext);

  useEffect(() => {
    // Sobald jemand /demo/... aufruft, CustomerID = "10001" 
    setSelectedCustomerId("10001");
  }, [setSelectedCustomerId]);

  return children;
}

/* 
  CombinedDashboard:
  Entscheidet anhand der Fensterbreite, ob das mobile
  oder das normale Dashboard gerendert wird.
*/
function CombinedDashboard() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth < 768);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile ? <MobileDashboard /> : <Dashboard />;
}

function App() {
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);

  return (
    <CustomerContext.Provider value={{ selectedCustomerId, setSelectedCustomerId }}>
      <Routes>
        {/* 1) Login/Signup (ohne Navbar) */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />

        {/* 2) Demo-Routen (kein ProtectedRoute), ID=10001 */}
        <Route
          path="/demo/dashboard"
          element={<ExternalRedirect url="https://www.prosperly.de" />}
        />
        <Route 
          path="/demo/properties" 
          element={
            <DemoRoute>
              <Navbar />
              <Properties />
              <Footer />
            </DemoRoute>
          }
        />
        <Route 
          path="/demo/documents" 
          element={
            <DemoRoute>
              <Navbar />
              <Documents />
              <Footer />
            </DemoRoute>
          }
        />
        <Route 
          path="/demo/contact" 
          element={
            <DemoRoute>
              <Navbar />
              <Contact />
              <Footer />
            </DemoRoute>
          }
        />
        <Route 
          path="/demo/settings" 
          element={
            <DemoRoute>
              <Navbar />
              <Settings />
              <Footer />
            </DemoRoute>
          }
        />

        {/* 3) Geschützte Routen (Login nötig) */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Navbar />
              {/* Hier ebenfalls Mobile oder Desktop */}
              <CombinedDashboard />
              <Footer />
            </ProtectedRoute>
          }
        />
        <Route
          path="/properties"
          element={
            <ProtectedRoute>
              <Navbar />
              <Properties />
              <Footer />
            </ProtectedRoute>
          }
        />
        <Route
          path="/documents"
          element={
            <ProtectedRoute>
              <Navbar />
              <Documents />
              <Footer />
            </ProtectedRoute>
          }
        />
        <Route
          path="/contact"
          element={
            <ProtectedRoute>
              <Navbar />
              <Contact />
              <Footer />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Navbar />
              <Settings />
              <Footer />
            </ProtectedRoute>
          }
        />

        {/* 4) Fuß-Bereich (öffentlich), mit Navbar + Footer */}
        <Route
          path="/agb"
          element={
            <>
              <Navbar />
              <AGB />
              <Footer />
            </>
          }
        />
        <Route
          path="/datenschutz"
          element={
            <>
              <Navbar />
              <Datenschutz />
              <Footer />
            </>
          }
        />
        <Route
          path="/impressum"
          element={
            <>
              <Navbar />
              <Impressum />
              <Footer />
            </>
          }
        />

        {/* 5) Start- und Fallback-Routen */}
        <Route path="/" element={<Navigate to="/dashboard" />} />
        <Route path="*" element={<Navigate to="/dashboard" />} />
      </Routes>
    </CustomerContext.Provider>
  );
}

export default App;
//some changes here 